import React from 'react';
import { PenTool, SquareLibrary, LayoutGrid, DollarSign, Heart, Gamepad2, ShoppingBag, Grid3X3, SquarePlus } from 'lucide-react';
import SidebarHeader from '@/components/ui/SidebarHeader';
import { SidebarFilterButton } from '@/components/ui/SidebarFilterButtons';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useAppLibrary } from './AppLibraryContext';
import { useAppFavorites } from '@/hooks/app-library/use-app-favorites';
import MobileSidebarAccordion from '@/components/ui/MobileSidebarAccordion';
// Separator removed

interface AppLibrarySidebarProps {
  onRequestApp?: (category?: string) => void;
}

const AppLibrarySidebar: React.FC<AppLibrarySidebarProps> = ({ onRequestApp }) => {
  const { selectedCategory, setSelectedCategory } = useAppLibrary();
  const { appItems, userFavorites, isLoading } = useAppFavorites();

  // Calculate counts dynamically from the database, but show structure immediately
  const totalAppsCount = isLoading ? null : appItems.length;
  const productivityAppsCount = isLoading ? null : appItems.filter(app => app.category === 'productivity').length;
  const creativeAppsCount = isLoading ? null : appItems.filter(app => app.category === 'creative').length;
  const educationAppsCount = isLoading ? null : appItems.filter(app => app.category === 'education').length;
  const financeAppsCount = isLoading ? null : appItems.filter(app => app.category === 'finance').length;
  const healthAppsCount = isLoading ? null : appItems.filter(app => app.category === 'health').length;
  const entertainmentAppsCount = isLoading ? null : appItems.filter(app => app.category === 'entertainment').length;
  const shoppingAppsCount = isLoading ? null : appItems.filter(app => app.category === 'shopping').length;

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
  };

  const SidebarContent = () => (
    <div className="space-y-6 pt-2">
      {/* Categories - Structure visible immediately, counts load dynamically */}
      <div className="space-y-2">
        {/* All Apps Filter */}
        <SidebarFilterButton
          variant="orange"
          isActive={selectedCategory === 'all'}
          count={totalAppsCount}
          onClick={() => handleCategoryChange('all')}
        >
          <Grid3X3 className="text-orange-500" />
          <span>All Apps</span>
        </SidebarFilterButton>
        
        <SidebarFilterButton
          variant="amber"
          isActive={selectedCategory === 'productivity'}
          count={productivityAppsCount}
          onClick={() => handleCategoryChange('productivity')}
        >
          <LayoutGrid className="text-amber-500" />
          <span>Productivity</span>
        </SidebarFilterButton>
        
        <SidebarFilterButton
          variant="green"
          isActive={selectedCategory === 'creative'}
          count={creativeAppsCount}
          onClick={() => handleCategoryChange('creative')}
        >
          <PenTool className="text-green-500" />
          <span>Creative</span>
        </SidebarFilterButton>
        
        <SidebarFilterButton
          variant="blue"
          isActive={selectedCategory === 'education'}
          count={educationAppsCount}
          onClick={() => handleCategoryChange('education')}
        >
          <SquareLibrary className="text-blue-500" />
          <span>Education</span>
        </SidebarFilterButton>
        
        <SidebarFilterButton
          variant="purple"
          isActive={selectedCategory === 'finance'}
          count={financeAppsCount}
          onClick={() => handleCategoryChange('finance')}
        >
          <DollarSign className="text-purple-500" />
          <span>Finance</span>
        </SidebarFilterButton>
        
        <SidebarFilterButton
          variant="red"
          isActive={selectedCategory === 'health'}
          count={healthAppsCount}
          onClick={() => handleCategoryChange('health')}
        >
          <Heart className="text-red-500" />
          <span>Health</span>
        </SidebarFilterButton>
        
        <SidebarFilterButton
          variant="indigo"
          isActive={selectedCategory === 'entertainment'}
          count={entertainmentAppsCount}
          onClick={() => handleCategoryChange('entertainment')}
        >
          <Gamepad2 className="text-indigo-500" />
          <span>Entertainment</span>
        </SidebarFilterButton>
        
        <SidebarFilterButton
          variant="orange"
          isActive={selectedCategory === 'shopping'}
          count={shoppingAppsCount}
          onClick={() => handleCategoryChange('shopping')}
        >
          <ShoppingBag className="text-orange-500" />
          <span>Shopping</span>
        </SidebarFilterButton>
      </div>
      
      {/* Request an App Button - Moved to bottom */}
      {onRequestApp && (
        <Button
          onClick={() => onRequestApp()}
          variant="default"
          className="w-full justify-center h-10 bg-gradient-to-r from-[var(--brand-gradient-from)] to-[var(--brand-gradient-to)] hover:opacity-90 text-white font-medium shadow-lg hover:shadow-xl transition-all duration-200"
        >
          <SquarePlus size={16} className="mr-2" />
          <span>Request an App</span>
        </Button>
      )}
    </div>
  );

  return (
    <div className="h-full">
      <MobileSidebarAccordion
        persistKey="app-library-sidebar"
        defaultExpanded={false}
        variant="purple"
        className="h-full"
      >
        <SidebarHeader title="Browse Apps" icon={LayoutGrid} variant="purple" />
        <SidebarContent />
      </MobileSidebarAccordion>
    </div>
  );
};

export default AppLibrarySidebar;
